// Deterministic mock sales report for design/QA — served when the Insights URL
// carries `?mock=true`. It bypasses the API and hands the view a fully-shaped
// normalized report (the same shape `normalizeSalesReport` produces) so every
// card, donut and chart renders with plausible multi-currency data.
//
// BRL is national, USD international; the two currencies are NEVER summed — each
// carries its own gross/net/commission and per-month series (WP-13 contract M12).

// Period gross revenue per currency (integer cents): R$1.351.287,96 / US$3.548.533,43.
const GROSS_TOTAL = { BRL: 135128796, USD: 354853343 };
// Paid-charge counts. National leads the volume; international is lower-volume,
// higher-ticket — so the split donut reads national-first and the average
// tickets (gross / count) stay realistic (≈ R$108 / US$430).
const SALES_COUNT = { BRL: 12480, USD: 8240 };

// Impact rates as a percentage of the period (what the cards render as "x% das
// vendas"): healthy refunds and chargebacks, so both note badges read green.
const REFUND_PCT = 1.6;
const CHARGEBACK_PCT = 0.4;

// Share of gross that reaches receivers (net) and the platform owner (their cut).
const NET_RATIO = 0.88;
const COMMISSION_RATIO = 0.70;

// Six-month month-over-month shape: growth steps all sit in [5%, 20%], with a
// dip in exactly 2 of the 6 months (−7% and −9%). Month 1 is the anchor.
const MOM_STEPS = [0.09, 0.14, -0.07, 0.18, -0.09];

// Relative weight of each of the 6 months, derived from MOM_STEPS.
const MONTH_WEIGHTS = MOM_STEPS.reduce(
    (acc, step) => [...acc, acc[acc.length - 1] * (1 + step)],
    [1],
);

// Split `total` across `weights` as integers that sum back to exactly `total`.
// Any rounding remainder lands on the largest bucket, so a small negative diff
// can never flip a tiny bucket below zero.
function distribute(total, weights) {
    const sum = weights.reduce((a, b) => a + b, 0) || 1;
    const out = weights.map(w => Math.round((total * w) / sum));
    const diff = total - out.reduce((a, b) => a + b, 0);
    let maxIdx = 0;
    out.forEach((v, i) => {
        if (v > out[maxIdx]) maxIdx = i;
    });
    out[maxIdx] += diff;
    return out;
}

function pad(n) {
    return String(n).padStart(2, '0');
}

function fmtDate(d) {
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

// The last `n` calendar months as {from,to} buckets ending with the current
// month, matching the `date.from`/`date.to` string format the charts parse.
function lastNMonths(n) {
    const now = new Date();
    const buckets = [];
    for (let i = n - 1; i >= 0; i--) {
        const from = new Date(now.getFullYear(), now.getMonth() - i, 1, 0, 0, 0);
        const to = new Date(now.getFullYear(), now.getMonth() - i + 1, 0, 23, 59, 59);
        buckets.push({ from: fmtDate(from), to: fmtDate(to) });
    }
    return buckets;
}

// Spread one month's revenue across the plans as a { planId: { amount, total } }
// map. Real plan ids (when available) keep the stacked-bar legend labels correct;
// a synthetic fallback keeps the chart populated for a plan-less community.
function splitAcrossPlans(monthRevenue, planList) {
    const weights = planList.map((_, i) => 1 / (i + 1.4));
    const amounts = distribute(monthRevenue, weights);
    const value = {};
    planList.forEach((plan, i) => {
        value[String(plan.id)] = {
            amount: amounts[i],
            total: Math.max(1, Math.round(amounts[i] / 18000)),
        };
    });
    return value;
}

function buildCurrencyReport(currency, buckets, planList) {
    const gross = GROSS_TOTAL[currency];
    const salesTotal = SALES_COUNT[currency];

    const revenueByMonth = distribute(gross, MONTH_WEIGHTS);
    const salesByMonth = distribute(salesTotal, MONTH_WEIGHTS);
    const granularity = { type: 'month', amount: 1 };

    return {
        currency,
        summary: {
            salesTotal,
            revenue: {
                total: gross,
                liquid: Math.round(gross * NET_RATIO),
                commission: Math.round(gross * COMMISSION_RATIO),
                averageTicket: Math.round(gross / salesTotal),
            },
            chargeback: {
                amount: Math.round((gross * CHARGEBACK_PCT) / 100),
                total: Math.round((salesTotal * CHARGEBACK_PCT) / 100),
                percentile: CHARGEBACK_PCT,
            },
            refund: {
                amount: Math.round((gross * REFUND_PCT) / 100),
                total: Math.round((salesTotal * REFUND_PCT) / 100),
                percentile: REFUND_PCT,
            },
        },
        methods: null,
        salesAmount: {
            granularity,
            data: buckets.map((date, i) => ({ date, value: salesByMonth[i] })),
        },
        revenue: {
            granularity,
            data: buckets.map((date, i) => ({ date, value: revenueByMonth[i] })),
        },
        productRevenue: {
            granularity,
            data: buckets.map((date, i) => ({ date, value: splitAcrossPlans(revenueByMonth[i], planList) })),
        },
    };
}

// A synthetic plan roster for when the selected community has no real plans to
// borrow ids/labels from — keeps the "Receita por plano" chart populated.
const FALLBACK_PLANS = [
    { id: 'mock-annual', label: 'Plano Anual' },
    { id: 'mock-monthly', label: 'Plano Mensal' },
    { id: 'mock-lifetime', label: 'Acesso Vitalício' },
    { id: 'mock-trial', label: 'Plano Experimental' },
];

/**
 * Build the mocked, already-normalized sales report.
 *
 * @param {Array<{id: number|string, label: string}>} [plans] Real plans of the
 *   selected community; used so the per-plan chart shows true plan labels.
 */
export default function buildMockReport(plans) {
    const planList = plans && plans.length ? plans.slice(0, 6) : FALLBACK_PLANS;
    const buckets = lastNMonths(6);

    return {
        currency: 'BRL',
        availableCurrencies: ['BRL', 'USD'],
        currencyReports: [
            buildCurrencyReport('BRL', buckets, planList),
            buildCurrencyReport('USD', buckets, planList),
        ],
    };
}
